import {
  BlockedReasonSchema,
  type BlockedReasonType,
} from './generated/inputTypeSchemas/BlockedReasonSchema';

export const BLOCKED_REASON_VALUES =
  BlockedReasonSchema.options as readonly BlockedReasonType[];

export const BLOCKED_STATUS_FILTER_PREFIX = 'blocked_' as const;

const SKIP_ATTACK_REASON_TYPE_TO_BLOCKED_REASON = {
  signal_blocked_by_guardrails: 'guardrails',
  lead_blocked_by_guardrails: 'guardrails',
  authentication_failure: 'authentication_failure',
  resume_max_retries_exceeded: 'tool_or_environment_error',
  provider_content_filter: 'content_filter',
} as const satisfies Record<string, BlockedReasonType>;

function parseBlockedReason(
  value: string | null | undefined,
): BlockedReasonType | null {
  const parsed = BlockedReasonSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
}

export function mapSkipAttackReasonTypeToBlockedReason({
  skipAttackReasonType,
}: {
  skipAttackReasonType: string | null | undefined;
}): BlockedReasonType | null {
  if (skipAttackReasonType == null) {
    return null;
  }

  if (
    !Object.hasOwn(
      SKIP_ATTACK_REASON_TYPE_TO_BLOCKED_REASON,
      skipAttackReasonType,
    )
  ) {
    return null;
  }

  return SKIP_ATTACK_REASON_TYPE_TO_BLOCKED_REASON[
    skipAttackReasonType as keyof typeof SKIP_ATTACK_REASON_TYPE_TO_BLOCKED_REASON
  ];
}

export function mapPostAttackBlockReasonToBlockedReason({
  blockReason,
}: {
  blockReason: string | null | undefined;
}): BlockedReasonType | null {
  return parseBlockedReason(blockReason);
}

function inferBlockedReasonFromFreeText(
  value: string | null | undefined,
): BlockedReasonType | null {
  if (value == null) {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized.length === 0) {
    return null;
  }

  if (normalized.includes('guardrail')) {
    return 'guardrails';
  }

  if (
    normalized.includes('jwt') ||
    normalized.includes('auth') ||
    normalized.includes('credential') ||
    normalized.includes('login') ||
    normalized.includes('otp') ||
    normalized.includes('password') ||
    normalized.includes('session')
  ) {
    return 'authentication_failure';
  }

  if (
    normalized.includes('content filter') ||
    normalized.includes('content_filter')
  ) {
    return 'content_filter';
  }

  if (normalized.includes('waf') || normalized.includes('firewall')) {
    return 'waf';
  }

  if (normalized.includes('rate limit') || normalized.includes('rate_limit')) {
    return 'rate_limit';
  }

  if (
    normalized.includes('anti-bot') ||
    normalized.includes('anti_bot') ||
    normalized.includes('bot protection')
  ) {
    return 'anti_bot_system';
  }

  if (
    normalized.includes('unreachable') ||
    normalized.includes('econnrefused')
  ) {
    return 'server_unreachable';
  }

  if (normalized.includes('artifact')) {
    return 'missing_artifacts';
  }

  if (
    normalized.includes('untested') ||
    normalized.includes('never tested') ||
    normalized.includes('did not test the hypothesis')
  ) {
    return 'untested';
  }

  return null;
}

export function resolveBlockedReasonFromObstruction({
  blockReason,
  skipAttackReasonType,
  skipAttackReason,
}: {
  blockReason: string | null | undefined;
  skipAttackReasonType: string | null | undefined;
  skipAttackReason?: string | null | undefined;
}): BlockedReasonType | null {
  return (
    mapPostAttackBlockReasonToBlockedReason({ blockReason }) ??
    mapSkipAttackReasonTypeToBlockedReason({ skipAttackReasonType }) ??
    mapSkipAttackReasonTypeToBlockedReason({
      skipAttackReasonType: blockReason,
    }) ??
    mapSkipAttackReasonTypeToBlockedReason({
      skipAttackReasonType: skipAttackReason,
    }) ??
    inferBlockedReasonFromFreeText(skipAttackReason) ??
    inferBlockedReasonFromFreeText(blockReason)
  );
}

export function getBlockedStatusFilterValue(
  reason: BlockedReasonType,
): BlockedStatusFilterValue {
  return `${BLOCKED_STATUS_FILTER_PREFIX}${reason}`;
}

export type BlockedStatusFilterValue =
  `${typeof BLOCKED_STATUS_FILTER_PREFIX}${BlockedReasonType}`;

export function blockedReasonFromStatusFilterValue(
  value: string,
): BlockedReasonType | null {
  if (!value.startsWith(BLOCKED_STATUS_FILTER_PREFIX)) {
    return null;
  }

  return parseBlockedReason(value.slice(BLOCKED_STATUS_FILTER_PREFIX.length));
}
